export const TAX_SUMMARY_MONTH_KEYS = [
  "ene",
  "feb",
  "mar",
  "abr",
  "may",
  "jun",
  "jul",
  "ago",
  "sep",
  "oct",
  "nov",
  "dic",
  "acumulado",
];

export const TAX_SUMMARY_MONTH_LABELS = {
  ene: "Ene",
  feb: "Feb",
  mar: "Mar",
  abr: "Abr",
  may: "May",
  jun: "Jun",
  jul: "Jul",
  ago: "Ago",
  sep: "Sep",
  oct: "Oct",
  nov: "Nov",
  dic: "Dic",
  acumulado: "Acum.",
};

/** Filas con casilla de desglose CFDI (solo lectura; no altera cálculos). */
export const TAX_SUMMARY_CFDI_DRILLDOWN_ROW_IDS = new Set([
  "isr_general.ventas_facturadas",
  "isr_general.ventas_facturadas_t16",
  "isr_general.ventas_facturadas_t8",
  "isr_general.ventas_facturadas_t0",
  "isr_general.ventas_facturadas_exentas",
  "isr_resico.ventas_facturadas",
  "isr_resico.ventas_facturadas_t16",
  "isr_resico.ventas_facturadas_t8",
  "isr_resico.ventas_facturadas_t0",
  "isr_resico.ventas_facturadas_exentas",
  "isr_resico.ventas_cobradas_gravadas",
  "isr_resico.ventas_cobradas_exentas",
  "isr_resico.gastos_gravados",
  "isr_resico.nominas_pagadas",
  "isr_resico.gastos_exentos_pagados",
  "iva.retenido_terceros",
  "iva.causado_mes",
  "iva.causado_mes_t16",
  "iva.causado_mes_t8",
  "iva.causado_mes_t0",
  "iva.acreditable_mes",
  "retenciones.resico",
  "retenciones.fletes",
  "retenciones.honorarios",
  "retenciones.arrendamiento",
  "retenciones.iva_terceros",
  "retenciones.ispt_salarios",
  "isr_general.acreditamiento_isr",
  "isr_resico.acreditamiento_isr",
]);

export function isTaxSummaryCfdiDrilldownRow(rowId) {
  return TAX_SUMMARY_CFDI_DRILLDOWN_ROW_IDS.has(String(rowId || "").trim());
}

/**
 * Fuente a enviar al detalle: si el mes (o todos los meses con valor) son captura manual.
 */
export function resolveTaxSummaryDrilldownCellSource(workbook, rowId, monthKey = "all") {
  const sources = workbook?.sources || {};
  const values = workbook?.values?.[rowId] || {};
  const mk = String(monthKey || "all").trim();
  if (mk && mk !== "all") {
    return String(sources[`${rowId}.${mk}`] || "");
  }
  const monthsWithValue = TAX_SUMMARY_MONTH_KEYS.filter((key) => {
    if (key === "acumulado") return false;
    const raw = values[key];
    return raw !== "" && raw != null;
  });
  if (!monthsWithValue.length) return "";
  if (monthsWithValue.every((key) => sources[`${rowId}.${key}`] === "manual")) {
    return "manual";
  }
  return "";
}

/** Ancho fijo por columna mensual para montos con separador de miles (evita recorte). */
export function taxSummaryMonthColumnClass(monthKey) {
  const isAcum = monthKey === "acumulado";
  const width = isAcum ? "min-w-[7.25rem] w-[7.25rem]" : "min-w-[6.25rem] w-[6.25rem]";
  return `${width} max-w-none whitespace-nowrap`;
}

export const TAX_SUMMARY_AMOUNT_CELL_CLASS =
  "h-7 min-w-0 w-full rounded-none border-0 px-1.5 text-[10px] text-right tabular-nums shadow-none focus-visible:ring-1 focus-visible:ring-inset";

/** Campo de formulario (encabezado): borde visible como los demás inputs. */
export const TAX_SUMMARY_AMOUNT_FIELD_CLASS =
  "h-8 w-full rounded-md border border-input bg-white px-3 text-xs tabular-nums shadow-sm focus-visible:ring-1";

export function buildTaxSummaryCapturePath({
  fiscalClientId,
  year,
  returnTo,
  returnLabel,
} = {}) {
  const client = String(fiscalClientId || "").trim();
  const yr = Number(year);
  const params = new URLSearchParams();
  if (client) params.set("client", client);
  if (Number.isFinite(yr) && yr >= 2000) params.set("year", String(yr));
  const back = String(returnTo || "").trim();
  if (back) params.set("returnTo", back);
  const label = String(returnLabel || "").trim();
  if (label) params.set("returnLabel", label);
  const qs = params.toString();
  return qs ? `/despacho/resumen-impuestos?${qs}` : "/despacho/resumen-impuestos";
}

export function cloneWorkbook(workbook) {
  const values = {};
  const srcValues = workbook?.values && typeof workbook.values === "object" ? workbook.values : {};
  Object.entries(srcValues).forEach(([label, monthMap]) => {
    values[label] = { ...(monthMap || {}) };
  });
  const baselines = {};
  const srcBaselines = workbook?.prefill_baselines && typeof workbook.prefill_baselines === "object"
    ? workbook.prefill_baselines
    : {};
  Object.entries(srcBaselines).forEach(([label, monthMap]) => {
    baselines[label] = { ...(monthMap || {}) };
  });
  const aux = workbook?.auxiliary_sheets;
  let auxiliary_sheets;
  if (aux && typeof aux === "object") {
    auxiliary_sheets = JSON.parse(JSON.stringify(aux));
  }
  return {
    version: workbook?.version || 2,
    year: workbook?.year,
    meta: { ...(workbook?.meta || {}) },
    values,
    sources: { ...(workbook?.sources || {}) },
    prefill_baselines: baselines,
    ...(auxiliary_sheets ? { auxiliary_sheets } : {}),
  };
}

export const TAX_SUMMARY_REGIMEN_OPTIONS = [
  { value: "general_ley", label: "Régimen general de ley (PM — Art. 14)" },
  { value: "resico", label: "RESICO / flujo de efectivo (PF o PM confianza)" },
];

/** Etiquetas del selector según tipo de persona del cliente. */
export function buildTaxSummaryRegimenOptions(clientFiscal) {
  const tipo = String(clientFiscal?.tipo_persona || "").toLowerCase();
  const isMoral = tipo === "moral";
  return [
    {
      value: "general_ley",
      label: isMoral
        ? "Régimen general de ley (Art. 14 — persona moral)"
        : "Régimen general de ley (persona física)",
    },
    {
      value: "resico",
      label: isMoral
        ? "Régimen de confianza (RESICO — persona moral)"
        : "Persona física — ISR por flujo (RESICO / actividad)",
    },
  ];
}

export function effectiveTaxSummaryRegimen(workbook, clientFiscal) {
  const metaReg = String(workbook?.meta?.regimen || "").trim().toLowerCase();
  const suggested = String(clientFiscal?.suggested_regimen || "").trim().toLowerCase();
  if (!metaReg || metaReg === "todos") {
    return suggested || "general_ley";
  }
  if (workbook?.meta?.regimen_inferred === "true" || workbook?.meta?.regimen_inferred === true) {
    return suggested || metaReg || "general_ley";
  }
  return metaReg;
}

/** Alinea meta.regimen con el cliente cuando aún no hubo override manual. */
export function syncWorkbookRegimenFromClient(workbook, clientFiscal) {
  if (!clientFiscal) return workbook;
  const next = cloneWorkbook(workbook);
  const suggested = clientFiscal.suggested_regimen;
  if (!suggested) return next;
  const current = String(next.meta?.regimen || "").trim().toLowerCase();
  const inferred = !current || current === "todos"
    || next.meta?.regimen_inferred === "true"
    || next.meta?.regimen_inferred === true;
  if (inferred) {
    next.meta.regimen = suggested;
    next.meta.regimen_inferred = "true";
    if (clientFiscal.regimen_label) {
      next.meta.regimen_label = clientFiscal.regimen_label;
    }
    if (clientFiscal.regimen_fiscal) {
      next.meta.client_regimen_fiscal = clientFiscal.regimen_fiscal;
    }
    if (clientFiscal.tipo_persona) {
      next.meta.client_tipo_persona = clientFiscal.tipo_persona;
    }
  }
  return next;
}

export function visibleLayoutRows(layout, regimen = "general_ley", workbook = null) {
  const key = String(regimen || "general_ley").toLowerCase();
  const hideGeneral = key === "resico";
  const hideResico = key === "general_ley" || key === "general" || key.includes("general de ley");
  const values = workbook?.values && typeof workbook.values === "object" ? workbook.values : {};
  const breakdownVisible = breakdownRowsVisible(values);
  return (layout || []).filter((row) => {
    const section = row.section || "";
    if (hideGeneral && section === "isr_general") return false;
    if (hideResico && section === "isr_resico") return false;
    const rowId = row.row_id || "";
    if (row.breakdown_parent && !breakdownVisible.has(rowId)) {
      return false;
    }
    return true;
  });
}

export const TAX_SUMMARY_SECTION_ORDER = [
  "isr_general",
  "isr_resico",
  "iva",
  "retenciones",
  "otros",
];

/**
 * Agrupa filas visibles del resumen mensual por sección (ISR / IVA / retenciones).
 * Conserva rowIdx original para pegado Excel y navegación por teclado.
 */
export function groupTaxSummaryDisplaySections(displayLayout) {
  const buckets = new Map();
  (displayLayout || []).forEach((row, rowIdx) => {
    const section = String(row.section || "otros").trim() || "otros";
    if (!buckets.has(section)) {
      buckets.set(section, { section, title: section, rows: [] });
    }
    const bucket = buckets.get(section);
    if (row.kind === "section" && row.label) {
      bucket.title = String(row.label).trim() || bucket.title;
    }
    if (row.kind !== "section") {
      bucket.rows.push({ row, rowIdx });
    }
  });
  return TAX_SUMMARY_SECTION_ORDER
    .filter((key) => {
      const bucket = buckets.get(key);
      return bucket && bucket.rows.length > 0;
    })
    .map((key) => buckets.get(key));
}

/** Filas de desglose visibles solo si hay más de una tasa con saldo. */
export function breakdownRowsVisible(values = {}) {
  const visible = new Set();
  const groups = [
    {
      primary: {
        t16: "isr_general.ventas_facturadas_t16",
        t8: "isr_general.ventas_facturadas_t8",
        t0: "isr_general.ventas_facturadas_t0",
        exento: "isr_general.ventas_facturadas_exentas",
      },
      mirror: {
        t16: "isr_resico.ventas_facturadas_t16",
        t8: "isr_resico.ventas_facturadas_t8",
        t0: "isr_resico.ventas_facturadas_t0",
        exento: "isr_resico.ventas_facturadas_exentas",
      },
    },
    {
      primary: {
        t16: "iva.causado_mes_t16",
        t8: "iva.causado_mes_t8",
        t0: "iva.causado_mes_t0",
      },
      mirror: {},
    },
  ];
  const monthKeys = TAX_SUMMARY_MONTH_KEYS.filter((mk) => mk !== "acumulado");
  const hasBalance = (rowId) => {
    const monthMap = values[rowId];
    if (!monthMap || typeof monthMap !== "object") return false;
    return monthKeys.some((mk) => {
      const raw = String(monthMap[mk] || "").trim().replace(/,/g, "");
      if (!raw) return false;
      const num = Number(raw);
      return Number.isFinite(num) && Math.abs(num) >= 0.005;
    });
  };
  groups.forEach(({ primary, mirror }) => {
    const activeRates = new Set();
    Object.entries(primary).forEach(([rate, rowId]) => {
      if (hasBalance(rowId)) activeRates.add(rate);
    });
    Object.entries(mirror).forEach(([rate, rowId]) => {
      if (hasBalance(rowId)) activeRates.add(rate);
    });
    if (activeRates.size <= 1) return;
    activeRates.forEach((rate) => {
      if (primary[rate]) visible.add(primary[rate]);
      if (mirror[rate]) visible.add(mirror[rate]);
    });
  });
  return visible;
}

const PREFILL_BASELINE_SOURCES = new Set([
  "cfdi_auto",
  "confronta_snapshot",
  "visor_resico",
  "iva_derived",
]);

/** Monto de precálculo guardado (no se borra al editar manualmente). */
export function getPrefillBaseline(workbook, rowId, monthKey) {
  const text = workbook?.prefill_baselines?.[rowId]?.[monthKey];
  return text == null ? "" : String(text);
}

/**
 * Cuando la celda es manual y hay precálculo, diferencia capturado − precálculo.
 * @returns {{ baseline: string, captured: string, diff: number|null, hasDiff: boolean }|null}
 */
export function getManualVsPrefillDiff(workbook, rowId, monthKey) {
  const sourceKey = `${rowId}.${monthKey}`;
  const source = workbook?.sources?.[sourceKey];
  if (source !== "manual") return null;
  const baseline = getPrefillBaseline(workbook, rowId, monthKey);
  if (!baseline) return null;
  const captured = workbook?.values?.[rowId]?.[monthKey] ?? "";
  const baseNum = Number(parseTaxSummaryAmount(baseline) || NaN);
  const capNum = Number(parseTaxSummaryAmount(captured) || NaN);
  if (!Number.isFinite(baseNum) || !Number.isFinite(capNum)) {
    return { baseline, captured, diff: null, hasDiff: String(baseline) !== String(captured) };
  }
  const diff = Math.round((capNum - baseNum) * 100) / 100;
  return { baseline, captured, diff, hasDiff: diff !== 0 };
}

/** Filas del año con captura manual distinta al precálculo (todas las empresas / mismo libro). */
export function listManualVsPrefillDiffs(workbook, layoutRows = []) {
  const labelById = {};
  (layoutRows || []).forEach((row) => {
    if (row?.row_id) labelById[row.row_id] = row.label || row.row_id;
  });
  const out = [];
  const values = workbook?.values && typeof workbook.values === "object" ? workbook.values : {};
  Object.keys(values).forEach((rowId) => {
    TAX_SUMMARY_MONTH_KEYS.forEach((monthKey) => {
      if (monthKey === "acumulado") return;
      const info = getManualVsPrefillDiff(workbook, rowId, monthKey);
      if (!info || !info.hasDiff) return;
      out.push({
        rowId,
        monthKey,
        label: labelById[rowId] || rowId,
        ...info,
      });
    });
  });
  return out;
}

export function patchWorkbookCell(workbook, rowId, monthKey, value) {
  const next = cloneWorkbook(workbook);
  if (!next.values[rowId]) next.values[rowId] = {};
  if (!next.prefill_baselines) next.prefill_baselines = {};
  if (!next.prefill_baselines[rowId]) next.prefill_baselines[rowId] = {};
  const prevValue = workbook?.values?.[rowId]?.[monthKey] ?? "";
  const sourceKey = `${rowId}.${monthKey}`;
  const prevSource = next.sources[sourceKey];
  // Conserva el precálculo la primera vez que se edita una celda precargada.
  if (
    value !== prevValue
    && PREFILL_BASELINE_SOURCES.has(prevSource)
    && !String(next.prefill_baselines[rowId][monthKey] || "").trim()
    && String(prevValue || "").trim()
  ) {
    next.prefill_baselines[rowId][monthKey] = prevValue;
  }
  next.values[rowId][monthKey] = value;
  if (value !== prevValue) {
    // Edición del usuario: deja de regenerarse desde IVA/CFDI automático.
    if (
      prevSource === "cfdi_auto"
      || prevSource === "confronta_snapshot"
      || prevSource === "iva_derived"
      || prevSource === "iva_locked"
      || prevSource === "calculated"
      || prevSource === "visor_resico"
    ) {
      if (value === "") delete next.sources[sourceKey];
      else next.sources[sourceKey] = "manual";
    }
  }
  if (!next.sources[sourceKey] && value !== "") {
    next.sources[sourceKey] = "manual";
  }
  return next;
}

/** Aplica varios cambios de celda y recalcula fuentes en un solo clone final. */
export function patchWorkbookCells(workbook, patches) {
  let next = workbook;
  (patches || []).forEach(({ rowId, monthKey, value }) => {
    next = patchWorkbookCell(next, rowId, monthKey, value);
  });
  return next;
}

/** Filas RESICO cuyo subtotal se sugiere desde IVA ÷ 0.16. */
export const RESICO_IVA_SUBTOTAL_ROW_IDS = new Set([
  "isr_resico.ventas_cobradas_gravadas",
  "isr_resico.gastos_gravados",
]);

export function isRowEditable(mode, readOnly) {
  if (readOnly) return false;
  return mode === "manual" || mode === "cfdi" || mode === "confirmacion";
}

export function isTaxSummaryMonthClosed(meta, monthKey) {
  const entry = meta?.month_closures?.[monthKey];
  return String(entry?.status || "open").toLowerCase() === "closed";
}

export function taxSummaryMonthClosureLabel(meta, monthKey) {
  return isTaxSummaryMonthClosed(meta, monthKey) ? "Cerrado" : "Abierto";
}

/** Solo enero capturable en saldo IVA años anteriores; arrastre en filas calculadas. */
export function isTaxSummaryCellEditable(rowId, mode, monthKey, readOnly = false, workbook = null) {
  if (!isRowEditable(mode, readOnly)) return false;
  if (monthKey !== "acumulado" && workbook && isTaxSummaryMonthClosed(workbook?.meta, monthKey)) {
    return false;
  }
  if (rowId === "iva.acumulado_anteriores") {
    return monthKey === "ene";
  }
  if (rowId === "isr_general.perdidas_fiscales") {
    return monthKey === "ene";
  }
  if (monthKey === "acumulado" && mode === "manual") {
    return false;
  }
  return true;
}

export function patchWorkbookMeta(workbook, patch) {
  const next = cloneWorkbook(workbook);
  next.meta = { ...next.meta, ...patch };
  return next;
}

export function patchAuxiliarySheets(workbook, auxiliarySheets) {
  const next = cloneWorkbook(workbook);
  next.auxiliary_sheets = JSON.parse(JSON.stringify(auxiliarySheets || {}));
  return next;
}

export function countCfdiAutoCells(workbook) {
  const sources = workbook?.sources || {};
  return Object.values(sources).filter(
    (v) => v === "cfdi_auto" || v === "confronta_snapshot",
  ).length;
}

/** Celdas editables en orden visual (fila × mes) para Enter / flechas. */
export function buildTaxSummaryNavigableCells(displayLayout, readOnly = false, workbook = null) {
  const cells = [];
  (displayLayout || []).forEach((row, rowIdx) => {
    const kind = row.kind || "row";
    if (kind !== "row" && kind !== "total") return;
    const mode = row.mode || "manual";
    const rowId = row.row_id || row.label || "";
    if (mode === "calculated" || mode === "coeficiente") return;
    TAX_SUMMARY_MONTH_KEYS.forEach((monthKey) => {
      if (!isTaxSummaryCellEditable(rowId, mode, monthKey, readOnly, workbook)) return;
      cells.push({ rowIdx, monthKey });
    });
  });
  return cells;
}

export function taxSummaryCellTestId(rowIdx, monthKey) {
  return `tax-summary-cell-${rowIdx}-${monthKey}`;
}

export function focusTaxSummaryCell(rowIdx, monthKey) {
  if (typeof document === "undefined") return;
  const el = document.querySelector(`[data-testid="${taxSummaryCellTestId(rowIdx, monthKey)}"]`);
  if (!el) return;
  el.focus({ preventScroll: false });
  if (typeof el.select === "function") {
    el.select();
  }
}

export function resolveTaxSummaryNavTarget(navCells, rowIdx, monthKey, direction) {
  const idx = navCells.findIndex((c) => c.rowIdx === rowIdx && c.monthKey === monthKey);
  if (idx < 0) return null;
  if (direction === "next") return navCells[idx + 1] || null;
  if (direction === "prev") return navCells[idx - 1] || null;
  const step = direction === "down" ? 1 : -1;
  if (direction === "down" || direction === "up") {
    for (let i = idx + step; i >= 0 && i < navCells.length; i += step) {
      if (navCells[i].monthKey === monthKey) return navCells[i];
    }
  }
  return null;
}

/** Quita separadores de miles; en captura parcial conserva el texto numérico en curso. */
export function parseTaxSummaryAmount(value, { partial = false, amountMode = "money" } = {}) {
  if (value === "" || value == null) return "";
  const cleaned = String(value).trim().replace(/,/g, "");
  if (partial) return cleaned;
  if (cleaned === "" || cleaned === "-" || cleaned === ".") return "";
  const num = Number(cleaned);
  if (!Number.isFinite(num)) return "";
  if (amountMode === "coeficiente") {
    return num.toFixed(4);
  }
  return cleaned;
}

/** Montos con coma de miles; coeficientes con 4 decimales (como Excel 0.0000). */
export function formatTaxSummaryAmount(value, amountMode = "money") {
  const raw = parseTaxSummaryAmount(value);
  if (!raw) return "";
  const num = Number(raw);
  if (!Number.isFinite(num)) return String(value ?? "");
  if (amountMode === "coeficiente") {
    return num.toLocaleString("es-MX", {
      minimumFractionDigits: 4,
      maximumFractionDigits: 4,
    });
  }
  // Visor / declaración provisional: pesos enteros sin centavos.
  if (amountMode === "pesos") {
    const rounded = num >= 0 ? Math.round(num) : -Math.round(-num);
    return rounded.toLocaleString("es-MX", {
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    });
  }
  return num.toLocaleString("es-MX", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

export const TAX_SUMMARY_IVA_PREFILL_ALGO = "confronta_contabilidad_v22_desglose";

const TAX_SUMMARY_MONTHLY_KEYS = TAX_SUMMARY_MONTH_KEYS.filter((k) => k !== "acumulado");

/** Detecta IVA causado corrupto (mayo vacío, algo viejo, meses idénticos). */
export function workbookNeedsIvaPatternRepair(workbook) {
  const wb = workbook || {};
  const algo = String(wb?.meta?.cfdi_iva_prefill_algo || "");
  if (algo !== TAX_SUMMARY_IVA_PREFILL_ALGO) return true;
  const causado = wb?.values?.["iva.causado_mes"] || {};
  const acred = wb?.values?.["iva.acreditable_mes"] || {};
  const filled = TAX_SUMMARY_MONTHLY_KEYS.filter((mk) => String(causado[mk] || "").trim());
  if (filled.length === 0) return false;
  if (filled.length >= 3 && !String(causado.may || "").trim()) return true;
  const ene = Number(String(causado.ene || "0").replace(/,/g, ""));
  const feb = Number(String(causado.feb || "0").replace(/,/g, ""));
  if (ene > 0.005 && Math.abs(ene - feb) < 0.01 && filled.length >= 2) return true;
  const sumC = filled.reduce((t, mk) => t + Math.abs(Number(String(causado[mk] || "0").replace(/,/g, "")) || 0), 0);
  const sumA = TAX_SUMMARY_MONTHLY_KEYS.reduce(
    (t, mk) => t + Math.abs(Number(String(acred[mk] || "0").replace(/,/g, "")) || 0),
    0,
  );
  if (sumA > 20000 && sumC > 0 && sumC < sumA * 0.35) return true;
  return false;
}

export function showTaxSummaryPfResicoPanel(clientFiscal, regimen) {
  if (clientFiscal?.show_pf_resico_tasa_panel === true) return true;
  const tipo = String(clientFiscal?.tipo_persona || "").toLowerCase();
  const regFiscal = String(clientFiscal?.regimen_fiscal || "").toLowerCase();
  const reg = String(regimen || "").toLowerCase();
  if (tipo !== "fisica" || reg !== "resico") return false;
  return (
    regFiscal.includes("626")
    || regFiscal.includes("confianza")
    || regFiscal.includes("resico")
  );
}

export function handleTaxSummaryGridKeyDown(event, { rowIdx, monthKey, navCells }) {
  const { key } = event;
  let direction = null;
  if (key === "Enter" || key === "ArrowRight") direction = "next";
  else if (key === "ArrowLeft") direction = "prev";
  else if (key === "ArrowDown") direction = "down";
  else if (key === "ArrowUp") direction = "up";
  else return;

  event.preventDefault();
  const target = resolveTaxSummaryNavTarget(navCells, rowIdx, monthKey, direction);
  if (target) focusTaxSummaryCell(target.rowIdx, target.monthKey);
}
